import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import {
  Input,
  InputContainer,
  HelperText,
  LabelContainer,
  RequiredIndicator,
} from '../../generic/form'
import { useStopInputScrollingIncrementNumber } from '../../../library/useStopInputScrollingIncrementNumber'
import InputNumberNoScrollWithUnit from '../../generic/InputNumberNoScrollWithUnit'

import InputValidationInfo from '../InputValidationInfo/InputValidationInfo'
import mermaidInputsPropTypes from '../mermaidInputsPropTypes'
import { IconButton } from '../../generic/buttons'
import { IconInfo } from '../../icons'

const InputNoRowWithLabelAndValidation = ({
  required = false,
  helperText = undefined,
  id,
  ignoreNonObservationFieldValidations = () => {},
  label,
  unignoreNonObservationFieldValidations = () => {},
  unit = undefined,
  validationMessages = [],
  validationType = undefined,
  renderItemWithinInput = undefined,
  renderItemAboveInput = undefined,
  renderInput = undefined,
  isInputDisabled = false,
  showHelperText = false,
  testId = undefined,
  ...restOfProps
}) => {
  const textFieldRef = useRef()

  useStopInputScrollingIncrementNumber(textFieldRef)

  const [internalShowHelperText, setInternalShowHelperText] = useState()

  useEffect(() => {
    setInternalShowHelperText(showHelperText)
  }, [showHelperText])

  const handleInfoIconClick = (event) => {
    setInternalShowHelperText(!internalShowHelperText)

    event.stopPropagation()
  }

  const inputProps = {
    id,
    'aria-labelledby': `aria-label${id}`,
    'aria-describedby': `aria-descp${id}`,
    'data-testid': testId ? `${testId}-input` : undefined,
  }

  const defaultInput = unit ? (
    <InputNumberNoScrollWithUnit
      {...inputProps}
      unit={unit}
      disabled={isInputDisabled}
      {...restOfProps}
    />
  ) : (
    <Input {...inputProps} {...restOfProps} ref={textFieldRef} />
  )

  const inputType = renderInput
    ? renderInput({ ...inputProps, unit, disabled: isInputDisabled })
    : defaultInput

  return (
    <>
      <LabelContainer>
        <label id={`aria-label${id}`} htmlFor={id}>
          {label}
        </label>
        <span>{required ? <RequiredIndicator /> : null}</span>
        {helperText ? (
          <IconButton
            id={`gtm-help-icon-${id}`}
            type="button"
            onClick={(event) => handleInfoIconClick(event, label)}
          >
            <IconInfo aria-label="info" id={`gtm-help-icon-svg-${id}`} />
          </IconButton>
        ) : null}
      </LabelContainer>

      <div>
        {renderItemAboveInput || null}
        <InputContainer>
          {inputType}

          {renderItemWithinInput || null}
        </InputContainer>
        {internalShowHelperText ? (
          <HelperText id={`aria-descp${id}`}>{helperText}</HelperText>
        ) : null}
      </div>

      <InputValidationInfo
        validationType={validationType}
        validationMessages={validationMessages}
        ignoreNonObservationFieldValidations={ignoreNonObservationFieldValidations}
        unignoreNonObservationFieldValidations={unignoreNonObservationFieldValidations}
        testId={testId ? `${testId}-validation` : undefined}
      />
    </>
  )
}

InputNoRowWithLabelAndValidation.propTypes = {
  required: PropTypes.bool,
  helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  showHelperText: PropTypes.bool,
  id: PropTypes.string.isRequired,
  isInputDisabled: PropTypes.bool,
  ignoreNonObservationFieldValidations: PropTypes.func,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  renderInput: PropTypes.func,
  renderItemAboveInput: PropTypes.node,
  renderItemWithinInput: PropTypes.node,
  unignoreNonObservationFieldValidations: PropTypes.func,
  testId: PropTypes.string,
  unit: PropTypes.string,
  validationMessages: mermaidInputsPropTypes.validationMessagesPropType,
  validationType: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
}

export default InputNoRowWithLabelAndValidation
